// DESIGN_SYSTEM_NOTES: wfm-modal-overlay / wfm-modal / wfm-modal-lg /
// wfm-modal-xl / wfm-modal-title and --wfm-* tokens only. No dialog library —
// the app ships @radix-ui/react-dialog but nothing imports it.
//
// The app's modal shell: overlay, backdrop-dismiss, Escape-to-close, focus
// affordances and a title row with a close button. Extracted verbatim from
// pages/workforce/wfShared.jsx (which now re-exports it) so the 24 pages that
// hand-roll `wfm-modal-overlay` have one shape to converge on.
//
//   <Modal title="Nuovo Webhook" onClose={close} size="lg">…</Modal>
//
// props:
//   title:     node — rendered in the title row, wired to aria-labelledby
//   onClose:   () => void — backdrop mousedown, Escape and the X button
//   size:      'sm' | 'lg' (default) | 'xl'
//   footer:    node (optional) — right-aligned action row under the body
//   closeOnBackdrop: boolean (default true) — off for destructive/dirty forms
//   escapeActive:    boolean (default true) — off while a nested confirm shows

import React, { useId } from 'react';
import { X } from 'lucide-react';
import useEscapeKey from '../../hooks/useEscapeKey';

const SIZE_CLASS = { sm: '', lg: 'wfm-modal-lg', xl: 'wfm-modal-xl' };

export default function Modal({
  title,
  onClose,
  children,
  size = 'lg',
  footer = null,
  closeOnBackdrop = true,
  escapeActive = true,
}) {
  const sizeClass = SIZE_CLASS[size] ?? SIZE_CLASS.lg;
  const titleId = useId();
  useEscapeKey(onClose, escapeActive);

  return (
    <div
      className="wfm-modal-overlay"
      onMouseDown={(e) => {
        if (closeOnBackdrop && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`wfm-modal ${sizeClass} max-h-[90vh] overflow-y-auto`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div
          className="flex items-center justify-between gap-3 pb-3 mb-4 border-b"
          style={{ borderColor: 'var(--wfm-border)' }}
        >
          <h3 className="wfm-modal-title" id={titleId}>{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 hover:bg-bg-hover transition-colors"
            aria-label="Chiudi"
          >
            <X size={18} style={{ color: 'var(--wfm-text-light)' }} />
          </button>
        </div>
        {children}
        {footer && (
          <div
            className="flex items-center justify-end gap-2 pt-4 mt-5 border-t"
            style={{ borderColor: 'var(--wfm-border)' }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
